import { computed, type ComputedRef, type Reactive } from 'vue';
import {
  hasDamageRankFeature,
  getLocationDispOfKp,
  validateAngle,
  validateComment,
} from 'src/lib/commentHelper';
import { lineBreakToBR } from 'src/lib/utils';
import { dtFormat } from 'src/lib/dateHelper';
import { downloadObjectUrl } from 'src/lib/downloadHelper';
import { extension as convertMimeTypeToExtension } from 'mime-types';
import type { CandidateFile } from 'src/models';
import type { GIKp } from 'src/models/geoItem';
import type { CommentEditProps } from 'src/models/comment';
import type { MastersContext } from 'src/models/master';
import { useAuth } from 'src/composables/useAuth';
import { COMMENT_SOURCE_TYPE_SELF } from 'src/consts/comment_source_type';
import type { MapElemInfoCommentState } from './useCommentEdit';

const COPY_LINK_TOOLTIP_TEXTS = {
  enabled: 'リンクをコピー',
  disabledOnHttp: 'http環境ではこの機能は使えません',
} as const;

interface CommentDisplayContext {
  iconPath: ComputedRef<string>;
  commentTypeDisp: ComputedRef<string>;
  shareScopeDisp: ComputedRef<string>;
  contentDisp: ComputedRef<string>;
  isParentComment: ComputedRef<boolean>;
  canModerateComment: ComputedRef<boolean>;
  canCopyLink: ComputedRef<boolean>;
  copyLinkTooltip: ComputedRef<string>;
  hasValidAngle: ComputedRef<boolean>;
  editIconPath: ComputedRef<string>;
  canUpdateTs: ComputedRef<boolean>;
  hasDamageRank: ComputedRef<boolean>;
  editItemHasValidAngle: ComputedRef<boolean>;
  isFixedCandidateImages: ComputedRef<Blob[] | undefined>;
  canSave: ComputedRef<boolean>;
  showCancelButton: ComputedRef<boolean>;
  kpDisp: ComputedRef<string>;
  downloadFile: (file: CandidateFile) => void;
}

export function useCommentDisplay(
  props: Required<CommentEditProps>,
  state: MapElemInfoCommentState,
  masters: Reactive<MastersContext>,
  commentKpObject: ComputedRef<GIKp | null | undefined>,
): CommentDisplayContext {
  const auth = useAuth();

  const downloadFile = (file: CandidateFile) => {
    const savedFile = file.content;
    const commentTs = dtFormat(state.comment.ts, 'yyyymmddHHMMSS');
    const timestamp = dtFormat(new Date(), 'yyyymmddHHMMSS');
    const extension = convertMimeTypeToExtension(file.type);
    const downloadFilenameTemplate = `付箋添付ファイル_${state.comment.id}_${commentTs}_${timestamp}.${extension}`;
    downloadObjectUrl(savedFile, file.name || downloadFilenameTemplate);
  };

  const iconPath = computed(() => {
    const commentType = masters.comment.commentTypeMap[state.comment.commentType];
    return commentType?.iconPath || '/static/img/comment_icon_04.png';
  });
  const commentTypeDisp = computed(() => {
    return masters.comment.commentTypeMap[state.comment.commentType]?.commentTypeDisp || '';
  });
  const shareScopeDisp = computed(() => {
    return masters.lov.shareScopeMap[state.comment.shareScope]?.val || '';
  });
  const contentDisp = computed(() => lineBreakToBR(state.comment.content));
  const isParentComment = computed(() => !state.comment.parentId);
  const isCommentOwner = computed(() => !!auth.user && auth.user.id === state.comment.userId);
  const canModerateComment = computed(() => {
    return isCommentOwner.value || !!auth.user?.hasRoleOperationsAdmin;
  });
  const canCopyLink = computed(() => {
    return !!window.navigator.clipboard;
  });
  const copyLinkTooltip = computed(() => {
    return canCopyLink.value ? COPY_LINK_TOOLTIP_TEXTS.enabled : COPY_LINK_TOOLTIP_TEXTS.disabledOnHttp;
  });
  const hasValidAngle = computed(() => {
    return validateAngle(state.comment.angle);
  });
  const editIconPath = computed(() => {
    const commentType = masters.comment.commentTypeMap[state.editItem.commentType];
    return commentType?.iconPath || '/static/img/comment_icon_04.png';
  });
  const isNew = computed(() => {
    return !state.comment.id;
  });
  const canUpdateTs = computed(() => {
    return !isNew.value && !state.comment.parentId && state.comment.sourceType === COMMENT_SOURCE_TYPE_SELF;
  });
  const hasDamageRank = computed(() => {
    return hasDamageRankFeature(masters.comment.commentFeaturesBySourceType, state.comment.sourceType);
  });
  const editItemHasValidAngle = computed(() => {
    return validateAngle(state.editItem.angle);
  });
  const isFixedCandidateImages = computed(() => {
    // 動画プレイヤーから呼ばれたときは最初から候補画像が渡されている
    return props.elem?.candidateImages;
  });
  const canSave = computed(() => {
    const hasNoError = state.errorMsgs.length === 0;
    return validateComment(state.editItem) && hasNoError;
  });
  const showCancelButton = computed(() => {
    return props.forceShowCancelButton || !props.isCreateSingleNewElement;
  });
  const kpDisp = computed(() => {
    if (!commentKpObject.value) return '';
    return getLocationDispOfKp(commentKpObject.value, masters.kp.roadNameDispMap);
  });

  return {
    iconPath,
    commentTypeDisp,
    shareScopeDisp,
    contentDisp,
    isParentComment,
    canModerateComment,
    canCopyLink,
    copyLinkTooltip,
    hasValidAngle,
    editIconPath,
    canUpdateTs,
    hasDamageRank,
    editItemHasValidAngle,
    isFixedCandidateImages,
    canSave,
    showCancelButton,
    kpDisp,
    downloadFile,
  };
}
